import { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { FileText, ImageOff, Plus, RefreshCw, Search, Trash2 } from 'lucide-react';
import * as installationApi from '@/api/installation.api';
import {
  agregarAlias,
  avisosDeModelo,
  filtrarModelos,
  parsearMeses,
  parsearPrecio,
  quitarAlias,
  type ModeloEnEdicion,
} from '@/lib/catalogo';
import { etiquetaCobertura } from '@/lib/cierre-validacion';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card, CardBody, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { Textarea } from '@/components/ui/Textarea';
import { useToast } from '@/hooks/useToast';
import { EntradaDeFichas } from './EntradaDeFichas';
import { MediaThumb, UploadImageButton } from './MediaThumb';

/**
 * Pestaña CATÁLOGO: los modelos que el negocio vende, uno por tarjeta.
 *
 * Antes era una rejilla de campos que fingía ser una tabla: trece modelos por
 * siete columnas dejaban noventa y pico de casillas en pantalla a la vez, con
 * los encabezados solo en la primera fila y las fotos escritas a mano como
 * `catalog/slug.jpg`. Ahora se ve una tarjeta por modelo —foto, nombre, precio
 * y lo que le falta— y se edita de a uno en su ficha.
 *
 * La ficha incluye los MESES DE GARANTÍA, que vivían en otra pestaña. Ver
 * `lib/catalogo.ts`: en la base siguen siendo dos listas, en pantalla es una.
 */

/** Tono de cada aviso: el de garantía es el que termina en un cliente esperando. */
const TONO_DE_AVISO = {
  'sin garantía cargada': 'warning',
  'sin precio': 'warning',
  'sin foto': 'default',
  'sin descripción': 'default',
} as const;

/** Una fila nueva, vacía: el dueño le pone nombre en la ficha que se abre. */
const MODELO_NUEVO: ModeloEnEdicion = {
  nombre: '',
  precio: '',
  nota: '',
  descripcion: '',
  alias: [],
  imagenes: [],
  mesesGarantia: '',
  idGarantia: '',
};

export function CatalogoTab({
  filas,
  onChange,
  currencyPrefix,
  catalogPdfKey,
}: {
  filas: ModeloEnEdicion[];
  onChange: (filas: ModeloEnEdicion[]) => void;
  /** Prefijo de moneda del negocio (RD$), para leer el precio como se cobra. */
  currencyPrefix: string;
  catalogPdfKey: string | null;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const [busqueda, setBusqueda] = useState('');
  /** Índice del modelo abierto en la ficha. null = ninguna ficha abierta. */
  const [abierto, setAbierto] = useState<number | null>(null);
  /** Índice del modelo que se está por quitar (se pregunta antes, con su nombre). */
  const [porBorrar, setPorBorrar] = useState<number | null>(null);

  const visibles = useMemo(
    () => filtrarModelos(filas, busqueda).map((f) => ({ fila: f, indice: filas.indexOf(f) })),
    [filas, busqueda],
  );

  /** Reemplaza UNA fila del catálogo (el resto queda igual). */
  function cambiar(indice: number, patch: Partial<ModeloEnEdicion>) {
    onChange(filas.map((f, i) => (i === indice ? { ...f, ...patch } : f)));
  }

  const regenPdf = useMutation({
    mutationFn: installationApi.regenerateCatalogPdf,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['installation'] });
      toast.success('PDF del catálogo regenerado');
    },
    onError: () => toast.error('No se pudo generar el PDF'),
  });

  const subirFoto = useMutation({
    mutationFn: async ({ file, indice }: { file: File; indice: number }) => ({
      indice,
      stored: await installationApi.uploadCatalogImage(file),
    }),
    onSuccess: ({ indice, stored }) => {
      const fila = filas[indice];
      if (!fila) return;
      // La recién subida va PRIMERA: así se previsualiza de inmediato aunque el
      // modelo arrastre claves viejas que apuntan a archivos que no existen.
      cambiar(indice, { imagenes: [stored.key, ...fila.imagenes] });
      toast.success('Foto subida — guarda el Catálogo para que quede');
    },
    onError: () => toast.error('No se pudo subir la foto'),
  });

  // "Quitar rotas": le pregunta al SERVIDOR qué claves no tienen archivo (es la
  // fuente de verdad; el onError del <img> mentiría si /media está caído).
  const quitarRotas = useMutation({
    mutationFn: async ({ indice, keys }: { indice: number; keys: string[] }) => ({
      indice,
      result: await installationApi.checkCatalogImages(keys),
    }),
    onSuccess: ({ indice, result }) => {
      const fila = filas[indice];
      if (!fila) return;
      if (result.missing.length === 0) {
        toast.success('Todas las fotos de este modelo tienen archivo');
        return;
      }
      const faltantes = new Set(result.missing);
      cambiar(indice, { imagenes: fila.imagenes.filter((k) => !faltantes.has(k)) });
      toast.success(`${result.missing.length} foto(s) sin archivo quitada(s) — recuerda guardar`);
    },
    onError: () => toast.error('No se pudo verificar las fotos'),
  });

  function agregarModelo() {
    onChange([...filas, { ...MODELO_NUEVO }]);
    setAbierto(filas.length);
  }

  function confirmarBorrado() {
    if (porBorrar === null) return;
    onChange(filas.filter((_, i) => i !== porBorrar));
    setPorBorrar(null);
    setAbierto(null);
  }

  const fichaAbierta = abierto === null ? null : (filas[abierto] ?? null);
  const nombrePorBorrar =
    porBorrar === null ? '' : filas[porBorrar]?.nombre.trim() || 'este modelo sin nombre';

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle>Catálogo y precios</CardTitle>
            <CardDescription>
              Los modelos que el asistente cotiza, con su foto y su garantía. El PDF del menú se
              regenera solo al guardar; regenéralo a mano si acabas de subir fotos.
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            {catalogPdfKey && (
              <a href={`/media/${catalogPdfKey}`} target="_blank" rel="noreferrer">
                <Button variant="outline" size="sm">
                  <FileText className="mr-1 h-3.5 w-3.5" /> Ver PDF
                </Button>
              </a>
            )}
            <Button
              variant="outline"
              size="sm"
              loading={regenPdf.isPending}
              onClick={() => regenPdf.mutate()}
            >
              <RefreshCw className="mr-1 h-3.5 w-3.5" /> Regenerar PDF
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardBody className="space-y-4">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div className="relative w-full max-w-xs">
            <Search className="pointer-events-none absolute left-3 top-[2.15rem] h-4 w-4 text-slate-400" />
            <Input
              label="Buscar modelo"
              className="pl-9"
              placeholder="Nombre o alias"
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
            />
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-slate-500 dark:text-slate-400">
              {busqueda.trim()
                ? `${visibles.length} de ${filas.length} modelos`
                : `${filas.length} ${filas.length === 1 ? 'modelo' : 'modelos'}`}
            </span>
            <Button variant="secondary" size="sm" onClick={agregarModelo}>
              <Plus className="mr-1 h-4 w-4" /> Agregar modelo
            </Button>
          </div>
        </div>

        {filas.length === 0 && (
          <EmptyState
            title="Todavía no hay modelos"
            description="Sin catálogo, el asistente no puede cotizar nada. Agrega el primero."
            action={
              <Button variant="secondary" size="sm" onClick={agregarModelo}>
                <Plus className="mr-1 h-4 w-4" /> Agregar modelo
              </Button>
            }
          />
        )}

        {filas.length > 0 && visibles.length === 0 && (
          <EmptyState
            icon={Search}
            title={`Ningún modelo coincide con «${busqueda.trim()}»`}
            description="Prueba con parte del nombre, o con el alias con el que lo pide el cliente."
          />
        )}

        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {visibles.map(({ fila, indice }) => (
            <TarjetaDeModelo
              key={indice}
              fila={fila}
              currencyPrefix={currencyPrefix}
              onAbrir={() => setAbierto(indice)}
            />
          ))}
        </div>
      </CardBody>

      {/* ------------------------- LA FICHA DEL MODELO ------------------------ */}
      <Modal
        open={fichaAbierta !== null}
        onClose={() => setAbierto(null)}
        size="xl"
        title={fichaAbierta?.nombre.trim() || 'Modelo nuevo'}
        description="Todo lo de este modelo, junto. Se guarda con el botón «Guardar Catálogo»."
        footer={
          <div className="flex w-full items-center justify-between gap-2">
            <Button variant="ghost" onClick={() => setPorBorrar(abierto)}>
              <Trash2 className="mr-1 h-4 w-4 text-rose-500" /> Quitar modelo
            </Button>
            <Button onClick={() => setAbierto(null)}>Listo</Button>
          </div>
        }
      >
        {fichaAbierta !== null && abierto !== null && (
          <FichaDeModelo
            fila={fichaAbierta}
            currencyPrefix={currencyPrefix}
            onCambiar={(patch) => cambiar(abierto, patch)}
            onSubirFoto={(file) => subirFoto.mutate({ file, indice: abierto })}
            subiendo={subirFoto.isPending && subirFoto.variables?.indice === abierto}
            onQuitarRotas={() =>
              quitarRotas.mutate({ indice: abierto, keys: fichaAbierta.imagenes })
            }
            verificando={quitarRotas.isPending && quitarRotas.variables?.indice === abierto}
          />
        )}
      </Modal>

      {/* Quitar un modelo se pregunta, y se pregunta CON SU NOMBRE: antes era un
          clic en un tacho que borraba la fila sin decir nada. */}
      <Modal
        open={porBorrar !== null}
        onClose={() => setPorBorrar(null)}
        title={`¿Quitar ${nombrePorBorrar} del catálogo?`}
        description="Los trabajos que ya lo usaron conservan lo que se les cargó; el asistente deja de ofrecerlo."
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setPorBorrar(null)}>
              Cancelar
            </Button>
            <Button variant="danger" onClick={confirmarBorrado}>
              Quitar
            </Button>
          </div>
        }
      >
        <p className="text-sm text-slate-600 dark:text-slate-300">
          Se quita también su período de garantía. El cambio se aplica cuando toques{' '}
          <strong>Guardar Catálogo</strong>.
        </p>
      </Modal>
    </Card>
  );
}

/** El precio como se lee en un presupuesto, o el aviso de que no hay. */
function precioLegible(precio: string, currencyPrefix: string): string {
  const { valor } = parsearPrecio(precio);
  if (valor === null) return 'Sin precio';
  return `${currencyPrefix} ${valor.toLocaleString('es-DO')}`;
}

/** Una tarjeta del grid: foto, nombre, precio y lo que le falta. */
function TarjetaDeModelo({
  fila,
  currencyPrefix,
  onAbrir,
}: {
  fila: ModeloEnEdicion;
  currencyPrefix: string;
  onAbrir: () => void;
}) {
  const avisos = avisosDeModelo(fila);
  return (
    <button
      type="button"
      onClick={onAbrir}
      className="flex w-full items-start gap-3 rounded-lg border border-surface-border p-3 text-left transition-colors hover:border-brand-400 hover:bg-slate-50 dark:border-slate-800 dark:hover:bg-slate-800/50"
    >
      <MediaThumb mediaKey={fila.imagenes[0] ?? null} alt={fila.nombre || 'modelo'} />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold text-slate-900 dark:text-slate-100">
          {fila.nombre.trim() || 'Modelo sin nombre'}
        </p>
        <p className="text-sm text-slate-600 dark:text-slate-300">
          {precioLegible(fila.precio, currencyPrefix)}
        </p>
        {avisos.length > 0 && (
          <div className="mt-1.5 flex flex-wrap gap-1">
            {avisos.map((a) => (
              <Badge key={a} variant={TONO_DE_AVISO[a]}>
                {a}
              </Badge>
            ))}
          </div>
        )}
      </div>
    </button>
  );
}

/** El formulario de UN modelo, dentro de su ficha. */
function FichaDeModelo({
  fila,
  currencyPrefix,
  onCambiar,
  onSubirFoto,
  subiendo,
  onQuitarRotas,
  verificando,
}: {
  fila: ModeloEnEdicion;
  currencyPrefix: string;
  onCambiar: (patch: Partial<ModeloEnEdicion>) => void;
  onSubirFoto: (file: File) => void;
  subiendo: boolean;
  onQuitarRotas: () => void;
  verificando: boolean;
}) {
  const precio = parsearPrecio(fila.precio);
  const meses = parsearMeses(fila.mesesGarantia);

  return (
    <div className="space-y-4">
      <div className="grid gap-4 md:grid-cols-2">
        <Input
          label="Nombre"
          value={fila.nombre}
          placeholder="P20"
          onChange={(e) => onCambiar({ nombre: e.target.value })}
        />
        <div>
          <Input
            label={`Precio (${currencyPrefix})`}
            value={fila.precio}
            placeholder="14995"
            error={precio.error ?? undefined}
            onChange={(e) => onCambiar({ precio: e.target.value })}
          />
          {!precio.error && (
            <p className="mt-1 text-xs text-slate-400">
              {precio.valor === null
                ? 'Sin precio no se ofrece en las cotizaciones, pero su garantía se sigue calculando.'
                : precioLegible(fila.precio, currencyPrefix)}
            </p>
          )}
        </div>
        <Input
          label="Nota"
          value={fila.nota}
          placeholder="+ batería adicional gratis"
          onChange={(e) => onCambiar({ nota: e.target.value })}
        />
        <div>
          <Input
            label="Garantía (meses)"
            value={fila.mesesGarantia}
            placeholder="12"
            error={meses.error ?? undefined}
            onChange={(e) => onCambiar({ mesesGarantia: e.target.value })}
          />
          <p className="mt-1 text-xs text-slate-400">
            {meses.valor === null
              ? 'Sin esto, cuando llegue una garantía de este modelo el panel no va a poder decir si entra en cobertura.'
              : etiquetaCobertura(meses.valor)}
          </p>
        </div>
      </div>

      <EntradaDeFichas
        label="Alias"
        valores={fila.alias}
        onAgregar={(texto) => onCambiar({ alias: agregarAlias(fila.alias, texto) })}
        onQuitar={(texto) => onCambiar({ alias: quitarAlias(fila.alias, texto) })}
        placeholder="Como lo llama el cliente"
        ayuda="Otras formas en que el cliente nombra este modelo. Escribe y aprieta Enter."
      />

      <div>
        <p className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">Fotos</p>
        <div className="flex flex-wrap items-center gap-2">
          {fila.imagenes.map((clave) => (
            <div key={clave} className="relative">
              <MediaThumb mediaKey={clave} alt={fila.nombre || 'modelo'} size="h-16 w-16" />
              <button
                type="button"
                aria-label={`Quitar la foto ${clave}`}
                onClick={() => onCambiar({ imagenes: fila.imagenes.filter((k) => k !== clave) })}
                className="absolute -right-1.5 -top-1.5 rounded-full border border-surface-border bg-white p-0.5 text-slate-400 shadow-sm hover:text-rose-600 dark:border-slate-700 dark:bg-slate-900"
              >
                <Trash2 className="h-3 w-3" />
              </button>
            </div>
          ))}
          {fila.imagenes.length === 0 && (
            <MediaThumb mediaKey={null} alt="sin foto" size="h-16 w-16" />
          )}
          <UploadImageButton label="Subir foto" loading={subiendo} onFile={onSubirFoto} />
          {fila.imagenes.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              loading={verificando}
              title="Quita las fotos cuyo archivo ya no está en el servidor"
              onClick={onQuitarRotas}
            >
              <ImageOff className="mr-1 h-3.5 w-3.5" /> Quitar rotas
            </Button>
          )}
        </div>
        <p className="mt-1 text-xs text-slate-400">
          Son las que el asistente manda por WhatsApp cuando le piden ver el modelo.
        </p>
      </div>

      <Textarea
        label="Descripción (va al PDF y al asistente)"
        rows={3}
        value={fila.descripcion}
        placeholder="Para quién es y qué lo hace especial"
        onChange={(e) => onCambiar({ descripcion: e.target.value })}
      />
    </div>
  );
}
